import axiosInstance from "@/lib/axiosInstance";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import type { TSearchParams } from "@/components/ui/Chips/FilterChip/types";
import type { FilterValue } from "@/store/tableFilterStore";
import { formBuilderEndpoints } from "./formBuilder.endpoints";
import type {
  FormBuilderDetailData,
  FormBuilderDetailResponse,
  FormBuilderListItem,
  FormVersionDetail,
  FormVersionResponse,
  FormVersionSummary,
  FormVersionsResponse,
} from "./formBuilder.types";

export const FormBuilderQueryKeys = {
  list: "formBuilderList",
  detail: "formBuilderDetail",
  versions: "formBuilderVersions",
  version: "formBuilderVersion",
  dropdown: "formBuilderDropdown",
  visitAssessment: "formBuilderVisitAssessment",
  visitResolvedForms: "formBuilderVisitResolvedForms",
  visitFormsPreview: "formBuilderVisitFormsPreview",
} as const;

const CSV_FILTER_KEYS = new Set([
  "status",
  "workOrderTypeId",
  "formTypeId",
  "tradeIds",
  "levelIds",
]);
const ID_FILTER_KEYS = new Set(["formTypeId", "workOrderTypeId", "tradeIds", "levelIds"]);

export interface FormBuilderListQueryOptions {
  page: number;
  pageSize: number;
  tenantId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: Array<{ filterBy: string; filterValue: FilterValue }>;
}

export interface FormBuilderListPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface FormBuilderListResult {
  data: FormBuilderListItem[];
  pagination?: FormBuilderListPagination;
}

export const getFormBuilderList = async (
  options: FormBuilderListQueryOptions,
  signal: AbortSignal,
): Promise<FormBuilderListResult> => {
  const params = new URLSearchParams();

  params.append("page", options.page.toString());
  params.append("limit", options.pageSize.toString());
  if (options.tenantId) params.append("tenantId", options.tenantId);

  if (options.search) {
    params.append("search", options.search);
  }

  if (options.sortBy) {
    params.append("sortBy", options.sortBy);
  }

  if (options.sortOrder) {
    params.append("sortOrder", options.sortOrder);
  }

  if (options.filters?.length) {
    const grouped = new Map<string, string[]>();
    for (const filter of options.filters) {
      const key = filter.filterBy;
      const raw = ID_FILTER_KEYS.has(key)
        ? String(filter.filterValue.id ?? filter.filterValue.value)
        : String(filter.filterValue.value);
      const existing = grouped.get(key) ?? [];
      if (!existing.includes(raw)) {
        existing.push(raw);
      }
      grouped.set(key, existing);
    }
    grouped.forEach((values, key) => {
      if (CSV_FILTER_KEYS.has(key)) {
        params.append(key, values.join(","));
      } else {
        values.forEach((value) => params.append(key, value));
      }
    });
  }

  const queryString = params.toString();
  const url = `${formBuilderEndpoints.forms}${queryString ? `?${queryString}` : ""}`;

  const response = await axiosInstance.get(url, { signal });
  const body = response.data;
  const payload = body?.data;

  if (Array.isArray(payload)) {
    return { data: payload, pagination: body?.pagination };
  }

  return {
    data: payload?.data ?? [],
    pagination: payload?.pagination ?? body?.pagination,
  };
};

const tenantQuery = (tenantId: string | undefined) =>
  tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : "";

export const getFormBuilderDetail = async (
  formId: string,
  tenantId: string | undefined,
  signal?: AbortSignal,
): Promise<FormBuilderDetailData> => {
  const response = await axiosInstance.get<FormBuilderDetailResponse>(
    `${formBuilderEndpoints.forms}/${encodeURIComponent(formId)}${tenantQuery(tenantId)}`,
    { signal },
  );
  return response.data.data;
};

export const getFormVersions = async (
  formId: string,
  tenantId: string | undefined,
  signal?: AbortSignal,
): Promise<FormVersionSummary[]> => {
  const response = await axiosInstance.get<FormVersionsResponse>(
    `${formBuilderEndpoints.formVersions(formId)}${tenantQuery(tenantId)}`,
    { signal },
  );
  return response.data.data;
};

export const getFormVersion = async (
  formId: string,
  version: number,
  tenantId: string | undefined,
  signal?: AbortSignal,
): Promise<FormVersionDetail> => {
  const response = await axiosInstance.get<FormVersionResponse>(
    `${formBuilderEndpoints.formVersion(formId, version)}${tenantQuery(tenantId)}`,
    { signal },
  );
  return response.data.data;
};

const FORM_STATUS_OPTIONS = [
  { id: "PUBLISHED", value: "PUBLISHED", label: "Published" },
  { id: "DRAFT", value: "DRAFT", label: "Draft" },
  { id: "ARCHIVED", value: "ARCHIVED", label: "Archived" },
];

export const getFormBuilderDropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
  searchParams?: TSearchParams,
): Promise<any> => {
  if (key === "status") {
    const term = search?.trim().toLowerCase();
    const items = term
      ? FORM_STATUS_OPTIONS.filter((opt) => opt.label.toLowerCase().includes(term))
      : FORM_STATUS_OPTIONS;
    return { data: items };
  }

  const params = new URLSearchParams();
  const limit = String(searchParams?.limit ?? 10);

  if (search) {
    params.append("search", search);
  }

  const url = "/dropdown";

  if (key === "workOrderType") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "work_order_type");
    params.append("modify", encodeURI(`{"source":["label"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("limit", limit);
  } else if (key === "formType") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "form_type");
    params.append("modify", encodeURI(`{"source":["label"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("limit", limit);
  } else if (key === "level") {
    params.append("type", "LookupTypes");
    params.append("field", "id");
    params.append("field", "label");
    params.append("filterBy", "type");
    params.append("filterValue", "form_level");
    params.append("modify", encodeURI(`{"source":["label"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("limit", limit);
  } else if (key === "trade") {
    params.append("type", "Trades");
    params.append("field", "id");
    params.append("field", "name");
    params.append("filterBy", "parentTradeId");
    params.append("filterValue", "null");
    params.append("modify", encodeURI(`{"source":["name"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("limit", limit);
  } else if (key === "formName") {
    params.append("type", "FormBuilderForm");
    params.append("field", "id");
    params.append("field", "name");
    params.append("limit", limit);
  } else if (key === "subTrade") {
    params.append("type", "Trades");
    params.append("field", "id");
    params.append("field", "name");
    params.append("filterBy", "parentTradeId");
    if (searchParams?.filterValue) {
      params.append("filterValue", searchParams.filterValue);
    } else {
      params.append("filterOperation", "neq");
      params.append("filterValue", "null");
    }
    params.append("modify", encodeURI(`{"source":["name"],"target":"value"}`));
    params.append("distinct", "true");
    params.append("limit", limit);
  }

  const queryString = getQueryParamsWithCustomQueryObj(params);
  const response = await axiosInstance.get(`${url}?${queryString}`, { signal });
  return response.data;
};
